import type { FC } from 'react';
import { Fragment, useMemo, useState } from 'react';
import type { BlockType } from '@domain/schemas/block.schema';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { mdiUnfoldLessHorizontal, mdiUnfoldMoreHorizontal } from '@mdi/js';
import { Icon } from '@mdi/react';
import { isClauseBlock } from '@root/helpers/blocks';
import { useLanguage } from '@root/src/language';
import sharedClasses from '@root/src/utils/shared-classes';
import { isClausesPath, isEndorsementPath, isSubmissionPath } from '@utils/app-paths';
import clsx from 'clsx';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { Clause } from './svg/Clause';
import { OneColumn } from './svg/OneColumn';
import { TwoColumn } from './svg/TwoColumn';
import { layoutSelectorClasses } from './classes';
import { SelectorOption } from './SelectorOption';
import type { BlockLayoutOption, LayoutOptionsProps } from './types';
import { findOptionWithFallback } from './utils';

const getLayoutOptions = (
  isContractPage: boolean,
  isEndorsementPage: boolean,
  isClausesPage: boolean,
  getContent: (key: string) => string,
  type: BlockType,
  showAllOptions: boolean,
): BlockLayoutOption[] => {
  const MRC_HEADING_BLOCK_OPTION: BlockLayoutOption = {
    type: 'mrc-heading',
    title: getContent('mrc-heading'),
    element: <TwoColumn />,
  };

  const CLAUSE_BLOCK_OPTION: BlockLayoutOption = {
    type: 'clause',
    title: getContent('clause'),
    element: <Clause />,
  };

  const CLAUSE_HEADING_BLOCK_OPTION: BlockLayoutOption = {
    type: 'clause-heading',
    title: getContent('clause-heading'),
    element: <TwoColumn />,
  };

  const GENERIC_BLOCK_OPTION: BlockLayoutOption = {
    type: 'generic',
    title: getContent('generic'),
    element: <OneColumn />,
  };

  if ((isContractPage && !isEndorsementPage) || showAllOptions) {
    return [MRC_HEADING_BLOCK_OPTION, GENERIC_BLOCK_OPTION, CLAUSE_BLOCK_OPTION, CLAUSE_HEADING_BLOCK_OPTION];
  }

  if (isClausesPage || isClauseBlock({ type })) {
    return [CLAUSE_BLOCK_OPTION, CLAUSE_HEADING_BLOCK_OPTION];
  }

  return [MRC_HEADING_BLOCK_OPTION, GENERIC_BLOCK_OPTION];
};

const LayoutOptions: FC<LayoutOptionsProps> = ({ isDisabled, onChange, showAllOptions = false, type }) => {
  const isContractPage = isSubmissionPath();
  const isEndorsementPage = isEndorsementPath();
  const isClausesPage = isClausesPath();
  const { getContent } = useLanguage({ prefix: 'naming.mrc.block.layout' });

  const options: BlockLayoutOption[] = useMemo(
    () => getLayoutOptions(isContractPage, isEndorsementPage, isClausesPage, getContent, type, showAllOptions),
    [getContent, isContractPage, isEndorsementPage, isClausesPage, type, showAllOptions],
  );

  const [selectedLayout, setSelectedLayout] = useState(findOptionWithFallback(options, type));

  useDeepCompareEffect(() => {
    setSelectedLayout(findOptionWithFallback(options, type));
  }, [options, type]);

  const handleChange = (value: BlockType) => {
    const option = findOptionWithFallback(options, value);
    setSelectedLayout(option);
    onChange(option.type);
  };

  return (
    <Listbox disabled={isDisabled} value={selectedLayout.type} onChange={handleChange}>
      {({ open }) => (
        <>
          <Label className="sr-only">Change block layout</Label>
          <div className="relative">
            <div className="inline-flex w-full rounded-md shadow-xs">
              <div
                className={clsx(
                  'relative z-0 inline-flex w-full rounded-md shadow-xs',
                  isDisabled && 'cursor-not-allowed',
                )}
              >
                <ListboxButton
                  data-testid="block-layout-list"
                  className={clsx(layoutSelectorClasses.button, isDisabled && sharedClasses.disabled)}
                >
                  <SelectorOption option={selectedLayout} />
                  <span className="sr-only">Change block layout</span>
                  <Icon
                    path={open ? mdiUnfoldLessHorizontal : mdiUnfoldMoreHorizontal}
                    className="text-info-400"
                    size={0.8}
                    aria-hidden="true"
                  />
                </ListboxButton>
              </div>
            </div>
            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions className={clsx(layoutSelectorClasses.optionsContainer)}>
                {options.map((option) => (
                  <ListboxOption
                    key={option.type}
                    className={({ focus }) =>
                      clsx(
                        focus ? 'bg-primary-50 text-white' : 'text-info-900',
                        'relative h-fit cursor-default list-none p-2 text-sm select-none',
                      )
                    }
                    value={option.type}
                    data-testid={`LayoutSelector:${option.type}`}
                  >
                    {({ selected, focus }) => <SelectorOption option={option} active={focus} selected={selected} />}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
};

export default LayoutOptions;
